import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function FHChildrenPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#f04e58]/5 via-white to-[#14B8A6]/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#f04e58]/10 text-[#f04e58] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-heart-pulse-line"></i>
              Pediatric Cardiovascular Health
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Familial Hypercholesterolemia (FH) in Children (Ages 0–20)
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Why Early Detection Changes a Lifetime
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Most people think high cholesterol is an adult problem.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              But for children born with <strong>Familial Hypercholesterolemia (FH)</strong>, elevated LDL ("bad") cholesterol is present from birth.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              That means artery damage can begin silently in childhood — long before symptoms ever appear.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Raising awareness about FH in children is critical because <strong>early diagnosis and treatment can prevent premature heart disease decades later</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* What Is FH */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            What Is Familial Hypercholesterolemia?
          </h2>
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              FH is an inherited genetic disorder that causes:
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-[#f04e58]/5 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-arrow-up-line text-2xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Very High LDL Cholesterol Levels</h3>
              </div>
              <div className="bg-orange-500/5 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-orange-500/10 mb-4">
                  <i className="ri-close-circle-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Impaired LDL Clearance</h3>
              </div>
              <div className="bg-rose-500/5 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-rose-500/10 mb-4">
                  <i className="ri-heart-line text-2xl text-rose-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Accelerated Atherosclerosis</h3>
              </div>
            </div>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-xl">
              <p className="text-gray-700 leading-relaxed mb-4">
                It affects approximately <strong>1 in 250 people worldwide</strong>.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                If one parent has FH (heterozygous FH), each child has a <strong>50% chance</strong> of inheriting the condition.
              </p>
              <p className="text-gray-700 leading-relaxed">
                A rarer and more severe form, <strong>Homozygous Familial Hypercholesterolemia (HoFH)</strong>, occurs when both parents pass on abnormal genes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why FH in Children Is So Serious */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Why FH in Children Is So Serious
          </h2>
          <div className="bg-gradient-to-br from-[#f04e58]/10 to-rose-500/10 rounded-2xl p-8 border border-[#f04e58]/20">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              <strong>LDL cholesterol exposure begins at birth.</strong>
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              This concept is known as <strong>"cholesterol-year burden"</strong> — the cumulative exposure to high LDL over time.
            </p>
            <div className="bg-white rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-4">In untreated FH:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-[#f04e58] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Fatty streaks can appear in arteries during childhood</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-[#f04e58] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Carotid artery thickening is measurable in adolescence</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-[#f04e58] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Early plaque formation can begin before age 20</span>
                </li>
              </ul>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed font-medium">
              By the time symptoms appear in adulthood, damage may already be advanced.
            </p>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img
              src="https://public.readdy.ai/ai/img_res/82950c7990ef6a460531f28b305a7d41.jpg"
              alt="Early detection and prevention in children with FH"
              className="w-full h-[600px] object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* What Happens If FH Is Not Treated */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
            What Happens If FH Is Not Treated?
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Untreated FH dramatically increases the risk of:
          </p>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Card 1 */}
            <div className="bg-gradient-to-br from-[#f04e58]/10 to-rose-500/10 rounded-2xl p-8 border border-[#f04e58]/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-lg flex-shrink-0">
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900">Premature Coronary Artery Disease</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Heart attacks in the 30s or 40s (sometimes earlier)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Multi-vessel coronary disease</span>
                </li>
              </ul>
            </div>

            {/* Card 2 */}
            <div className="bg-gradient-to-br from-orange-500/10 to-amber-500/10 rounded-2xl p-8 border border-orange-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-600 text-white font-bold text-lg flex-shrink-0">
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900">Stroke</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Carotid artery plaque</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Increased clot risk</span>
                </li>
              </ul>
            </div>

            {/* Card 3 */}
            <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-8 border border-purple-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-purple-600 text-white font-bold text-lg flex-shrink-0">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900">Aortic Valve Disease (especially in HoFH)</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-purple-600 text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Early calcification</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-purple-600 text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Supravalvular aortic stenosis</span>
                </li>
              </ul>
            </div>

            {/* Card 4 */}
            <div className="bg-gradient-to-br from-rose-500/10 to-red-500/10 rounded-2xl p-8 border border-rose-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-rose-600 text-white font-bold text-lg flex-shrink-0">
                  4
                </div>
                <h3 className="text-xl font-bold text-gray-900">Sudden Cardiac Death</h3>
              </div>
              <p className="text-gray-700">
                Sometimes the first symptom of undiagnosed FH
              </p>
            </div>
          </div>

          <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">In severe HoFH:</h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <i className="ri-alert-line text-red-600 text-lg flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-700">LDL levels may exceed 400–500 mg/dL</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-alert-line text-red-600 text-lg flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-700">Heart disease may develop in childhood</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-alert-line text-red-600 text-lg flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-700">Without treatment, life expectancy may be significantly shortened</span>
              </li>
            </ul>
          </div>

          <p className="text-xl text-center font-semibold text-gray-900">
            The tragedy is that much of this is <span className="text-[#14B8A6]">preventable</span>.
          </p>
        </div>
      </section>

      {/* Signs of FH in Children */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
            Signs of FH in Children
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Many children with FH appear completely healthy. However, possible signs include:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                <i className="ri-bar-chart-line text-2xl text-[#f04e58]"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">High LDL Levels</h3>
              <p className="text-sm text-gray-600">LDL cholesterol ≥190 mg/dL (or ≥160 mg/dL with family history)</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-orange-500/10 mb-4">
                <i className="ri-hand-heart-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Tendon Xanthomas</h3>
              <p className="text-sm text-gray-600">Cholesterol deposits in tendons</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-500/10 mb-4">
                <i className="ri-eye-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Xanthelasma</h3>
              <p className="text-sm text-gray-600">Yellow deposits around eyes</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-teal-500/10 mb-4">
                <i className="ri-eye-2-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Corneal Arcus</h3>
              <p className="text-sm text-gray-600">In adolescence</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-rose-500/10 mb-4">
                <i className="ri-parent-line text-2xl text-rose-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Family History</h3>
              <p className="text-sm text-gray-600">Strong family history of early heart disease</p>
            </div>
          </div>
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-xl">
            <p className="text-gray-700 font-medium">
              Because symptoms are often absent, <strong>screening is essential</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* When Should Children Be Tested */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
            When Should Children Be Tested?
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Professional guidelines recommend:
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Universal Screening */}
            <div className="bg-gradient-to-br from-[#14B8A6]/10 to-teal-500/10 rounded-2xl p-8 border border-[#14B8A6]/20">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#14B8A6] text-white mb-6">
                <i className="ri-shield-check-line text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Universal Screening</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Between ages <strong>9–11</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Again between ages <strong>17–21</strong></span>
                </li>
              </ul>
            </div>

            {/* Targeted Screening */}
            <div className="bg-gradient-to-br from-[#f04e58]/10 to-rose-500/10 rounded-2xl p-8 border border-[#f04e58]/20">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#f04e58] text-white mb-6">
                <i className="ri-focus-3-line text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Targeted Screening (Earlier)</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">As early as <strong>age 2</strong> if strong family history</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">If a parent has known FH</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">If a close relative had a heart attack before age 55</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-xl">
            <p className="text-gray-700 font-medium">
              A simple fasting lipid panel can identify elevated LDL levels.
            </p>
          </div>
        </div>
      </section>

      {/* How Is FH Diagnosed */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
            How Is FH Diagnosed in Children?
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Diagnosis may involve:
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Lipid Testing */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-lg flex-shrink-0">
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900">Lipid Testing</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-[#f04e58] text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">LDL ≥190 mg/dL strongly suggests FH</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-[#f04e58] text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">LDL ≥160 mg/dL with family history raises suspicion</span>
                </li>
              </ul>
            </div>

            {/* Family History */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-600 text-white font-bold text-lg flex-shrink-0">
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900">Family History</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-orange-600 text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Early heart disease in first-degree relatives</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-orange-600 text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Known FH mutation in parent</span>
                </li>
              </ul>
            </div>

            {/* Genetic Testing */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#14B8A6] text-white font-bold text-lg flex-shrink-0">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900">Genetic Testing</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-[#14B8A6] text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Confirms mutation in LDLR, APOB, or PCSK9 genes</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-[#14B8A6] text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Enables cascade screening of relatives</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
            <p className="text-gray-700 font-medium">
              Early diagnosis allows intervention before irreversible damage occurs.
            </p>
          </div>
        </div>
      </section>

      {/* Research Shows */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            What Does Research Show About Early Treatment?
          </h2>
          <div className="bg-gradient-to-br from-[#14B8A6]/10 to-emerald-500/10 rounded-2xl p-8 border border-[#14B8A6]/20">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Studies demonstrate that children with FH who start treatment early:
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#14B8A6]/10 mb-4">
                  <i className="ri-heart-pulse-line text-2xl text-[#14B8A6]"></i>
                </div>
                <p className="text-gray-700 font-medium">Show slower carotid artery thickening</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-500/10 mb-4">
                  <i className="ri-shield-check-line text-2xl text-emerald-600"></i>
                </div>
                <p className="text-gray-700 font-medium">Have significantly reduced cardiovascular risk in adulthood</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-teal-500/10 mb-4">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <p className="text-gray-700 font-medium">Achieve near-normal life expectancy</p>
              </div>
            </div>
            <p className="text-xl text-center font-semibold text-gray-900">
              Early LDL lowering dramatically reduces lifetime plaque burden.
            </p>
          </div>
        </div>
      </section>

      {/* Treatment Options */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Treatment Options for Children with FH
          </h2>
          
          {/* Treatment 1 */}
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl p-8 border border-green-500/20 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-600 text-white font-bold text-lg flex-shrink-0">
                1
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Lifestyle Intervention</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-xl p-4">
                <i className="ri-restaurant-line text-2xl text-green-600 mb-2"></i>
                <p className="text-gray-700 font-medium">Heart-healthy diet</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <i className="ri-run-line text-2xl text-green-600 mb-2"></i>
                <p className="text-gray-700 font-medium">Regular physical activity</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <i className="ri-smoke-line text-2xl text-green-600 mb-2"></i>
                <p className="text-gray-700 font-medium">Avoid smoking exposure</p>
              </div>
            </div>
            <p className="text-gray-700 italic">
              Lifestyle changes alone are usually not enough, but they are foundational.
            </p>
          </div>

          {/* Treatment 2 */}
          <div className="bg-gradient-to-br from-[#14B8A6]/10 to-teal-500/10 rounded-2xl p-8 border border-[#14B8A6]/20 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#14B8A6] text-white font-bold text-lg flex-shrink-0">
                2
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Statin Therapy</h3>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Statins are typically started between ages <strong>8–10</strong> in children with confirmed FH.
            </p>
            <div className="bg-white rounded-xl p-6 mb-4">
              <h4 className="font-semibold text-gray-900 mb-3">Research shows:</h4>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-[#14B8A6] text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Statins are generally safe in children</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-[#14B8A6] text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">They reduce LDL by 30–50%</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-[#14B8A6] text-lg flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Long-term studies demonstrate cardiovascular benefit</span>
                </li>
              </ul>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              Early initiation prevents decades of arterial damage.
            </p>
          </div>

          {/* Treatment 3 */}
          <div className="bg-gradient-to-br from-[#f04e58]/10 to-rose-500/10 rounded-2xl p-8 border border-[#f04e58]/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-lg flex-shrink-0">
                3
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Advanced Therapies (Severe Cases)</h3>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              For HoFH or severe cases:
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-4">
                <i className="ri-capsule-line text-2xl text-[#f04e58] mb-2"></i>
                <p className="text-gray-700 font-medium">Ezetimibe</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <i className="ri-syringe-line text-2xl text-[#f04e58] mb-2"></i>
                <p className="text-gray-700 font-medium">PCSK9 inhibitors (in adolescents)</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <i className="ri-heart-add-line text-2xl text-[#f04e58] mb-2"></i>
                <p className="text-gray-700 font-medium">LDL apheresis</p>
              </div>
            </div>
            <p className="text-gray-700 mt-4 italic">
              These therapies may be required to control extremely high LDL levels.
            </p>
          </div>
        </div>
      </section>

      {/* Cascade Screening */}
      <section className="py-16 bg-gradient-to-br from-[#14B8A6]/10 to-teal-500/10">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            The Importance of Cascade Screening
          </h2>
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              When one child is diagnosed with FH:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-[#14B8A6]/10 to-teal-500/10 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#14B8A6] text-white mb-4">
                  <i className="ri-parent-line text-2xl"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Parents should be tested</h3>
              </div>
              <div className="bg-gradient-to-br from-teal-500/10 to-emerald-500/10 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-teal-600 text-white mb-4">
                  <i className="ri-group-line text-2xl"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Siblings should be screened</h3>
              </div>
              <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-600 text-white mb-4">
                  <i className="ri-team-line text-2xl"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Extended family may need evaluation</h3>
              </div>
            </div>
            <div className="mt-8 bg-[#14B8A6]/10 border-l-4 border-[#14B8A6] p-6 rounded-r-xl">
              <p className="text-lg text-gray-900 font-semibold">
                Because FH is inherited, diagnosing one person can prevent multiple premature cardiac events within a family.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Emotional and Psychological Impact */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Emotional and Psychological Impact
          </h2>
          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-8 border border-purple-500/20">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              A diagnosis of FH in childhood can feel overwhelming.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-purple-500/10 mb-4">
                  <i className="ri-lightbulb-line text-2xl text-purple-600"></i>
                </div>
                <p className="text-gray-700 font-medium">It empowers families with knowledge</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-pink-500/10 mb-4">
                  <i className="ri-shield-check-line text-2xl text-pink-600"></i>
                </div>
                <p className="text-gray-700 font-medium">It allows proactive prevention</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-purple-500/10 mb-4">
                  <i className="ri-arrow-right-up-line text-2xl text-purple-600"></i>
                </div>
                <p className="text-gray-700 font-medium">It replaces uncertainty with action</p>
              </div>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed font-medium">
              Children who grow up understanding their condition are more likely to adhere to healthy habits long term.
            </p>
          </div>
        </div>
      </section>

      {/* The Power of Early Prevention */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
            The Power of Early Prevention
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            The difference between untreated and treated FH is profound.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Without Treatment */}
            <div className="bg-gradient-to-br from-red-500/10 to-rose-500/10 rounded-2xl p-8 border border-red-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-red-500 text-white">
                  <i className="ri-close-line text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Without Treatment</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">High LDL exposure continues for decades</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Plaque accumulates silently</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Risk of early heart attack rises dramatically</span>
                </li>
              </ul>
            </div>

            {/* With Early Treatment */}
            <div className="bg-gradient-to-br from-[#14B8A6]/10 to-emerald-500/10 rounded-2xl p-8 border border-[#14B8A6]/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#14B8A6] text-white">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">With Early Treatment</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-[#14B8A6] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">LDL burden drops</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-[#14B8A6] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Arterial damage slows</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-[#14B8A6] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Heart disease may be delayed or prevented entirely</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 bg-gradient-to-r from-[#14B8A6] to-emerald-500 rounded-2xl p-8 text-white text-center">
            <p className="text-xl font-semibold">
              Early diagnosis essentially rewrites the cardiovascular future.
            </p>
          </div>
        </div>
      </section>

      {/* Raising Awareness */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
            Raising Awareness
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Because FH often has no symptoms in childhood, it is frequently missed.
          </p>
          <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-2xl p-8 border border-amber-500/20">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              If your child has:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-xl p-6 flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-700">LDL above 160–190 mg/dL</span>
              </div>
              <div className="bg-white rounded-xl p-6 flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-700">A parent with high cholesterol</span>
              </div>
              <div className="bg-white rounded-xl p-6 flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-700">A family history of heart attack before age 55</span>
              </div>
              <div className="bg-white rounded-xl p-6 flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-gray-700">Known FH in the family</span>
              </div>
            </div>
            <p className="text-xl text-center font-semibold text-gray-900">
              Ask about evaluation for Familial Hypercholesterolemia.
            </p>
          </div>
        </div>
      </section>

      {/* Final Takeaway */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white/10 mx-auto mb-6">
              <i className="ri-heart-pulse-line text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl font-semibold mb-4">Final Takeaway</h2>
          </div>

          <div className="space-y-6 mb-8">
            <p className="text-lg text-white/90 leading-relaxed">
              Familial Hypercholesterolemia is a lifelong condition — but it does not have to mean lifelong heart disease.
            </p>
            
            <p className="text-xl text-white font-semibold text-center">
              The earlier FH is detected, the more we can prevent.
            </p>

            <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
              <p className="text-base text-white/90 mb-4">In children, early screening and treatment can:</p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-white/90">Prevent premature heart attacks</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-white/90">Protect arteries before damage accumulates</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-white/90">Safeguard future generations</span>
                </li>
              </ul>
            </div>

            <p className="text-lg text-white/90 leading-relaxed text-center">
              Awareness is the first step.
            </p>
          </div>

          <div className="bg-gradient-to-r from-[#14B8A6] to-emerald-500 rounded-xl p-8 backdrop-blur-sm text-center">
            <p className="text-xl font-semibold text-white">
              Because when it comes to FH, the earlier we act, the more lives we protect.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Early Detection Saves Lives
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            If you have concerns about FH in your child or family, schedule a consultation today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/consultation"
              className="inline-flex items-center justify-center gap-2 bg-[#14B8A6] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#0d9488] transition-colors whitespace-nowrap"
            >
              <i className="ri-calendar-line text-xl"></i>
              Schedule Consultation
            </Link>
            <Link
              to="/topics"
              className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-colors border border-white/20 whitespace-nowrap"
            >
              <i className="ri-book-open-line text-xl"></i>
              Explore More Topics
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
